/**
 * About - Simple content section with text and optional image
 * Replaces content-heavy sections like StartBlock with cleaner API
 */

import React from 'react'
import { Box, Container, CardMedia } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import TextBlock from '../blocks/TextBlock'

interface AboutProps {
	title: string
	content: string
	image?: string
	imageCaption?: string
	layout?: 'text-only' | 'text-above-image' | 'text-below-image'
	backgroundColor?: 'default' | 'white' | 'transparent'
	textAlign?: 'left' | 'center' | 'right'
}

const About: React.FC<AboutProps> = ({
	title,
	content,
	image,
	imageCaption,
	layout = 'text-above-image',
	backgroundColor = 'default',
	textAlign = 'center'
}) => {
	const theme = useTheme()

	const getBackgroundColor = () => {
		switch (backgroundColor) {
			case 'white':
				return theme.palette.common.white
			case 'transparent':
				return 'transparent'
			case 'default':
				return theme.palette.mode === 'dark' ? theme.palette.custom?.dark : 'inherit'
			default:
				return theme.palette.mode === 'dark' ? theme.palette.custom?.dark : 'inherit'
		}
	}

	const renderContent = () => (
		<TextBlock
			title={title}
			titleLevel={2}
			content={content}
			align={textAlign}
			spacing="normal"
			maxWidth={{ xs: '100%', md: '800px' }}
		/>
	)

	const renderImage = () =>
		image ? (
			<CardMedia
				sx={{ maxWidth: 605, mx: 'auto' }}
				component="img"
				image={image}
				alt={title}
				height={357}
				style={{ objectFit: 'cover' }}
			/>
		) : null

	return (
		<Box
			component="section"
			sx={{
				backgroundColor: getBackgroundColor(),
				py: { md: 5, xs: 2.5 }
			}}
		>
			<Container sx={{ position: 'relative', textAlign: textAlign }}>
				{layout === 'text-only' && renderContent()}

				{layout === 'text-above-image' && (
					<>
						{renderContent()}
						{image && <Box sx={{ mt: 4 }}>{renderImage()}</Box>}
					</>
				)}

				{layout === 'text-below-image' && (
					<>
						{image && <Box sx={{ mb: 4 }}>{renderImage()}</Box>}
						{renderContent()}
					</>
				)}
			</Container>
		</Box>
	)
}

export default About
